import { Badge, Checkbox, Grid, IconButton, List, ListItemText, Popover, Select, TextField } from '@mui/material';
import { FilterType, FilterTypeOperators, FilterValue } from './FilterableTable';
import { Check, FilterListOutlined } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { ListItem, MenuItem } from '@material-ui/core';
/* eslint-disable */
type FilterChangedFunc = (newValue: FilterValue) => void;
type MappingFunc = (value: string) => string;
/* eslint-enable */
interface ColumnFilterProps {
  type: FilterType;
  filterValue: FilterValue | undefined;
  values?: string[];
  onFilterChanged: FilterChangedFunc;
  getValueCaption?: MappingFunc;
}

export default function ColumnFilter({
  type,
  filterValue,
  values,
  onFilterChanged,
  getValueCaption,
}: ColumnFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [input, setInput] = useState<FilterValue | undefined>(filterValue);
  useEffect(() => {
    setInput(filterValue);
  }, [filterValue]);

  const handleClose = () => {
    setAnchorEl(null);
    setInput({ value: null, operator: undefined });
  };
  const handleApply = () => {
    onFilterChanged(input ? { ...input } : { value: undefined, operator: undefined });
    handleClose();
  };
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
      >
        <Badge color="primary" variant="dot" invisible={!filterValue || !filterValue.value?.length}>
          <FilterListOutlined />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
      >
        <Grid container direction={'column'} p={1}>
          {type === 'string' ? (
            FilterFormString(input, setInput, handleApply)
          ) : type === 'date' ? (
            FilterFormDate(input, setInput)
          ) : type === 'boolean' ? (
            FilterFormBoolean(input, setInput)
          ) : type === 'number' ? (
            FilterFormNumber(input, setInput, handleApply)
          ) : type === 'values' ? (
            FilterFormValues(input, values ?? [], setInput, getValueCaption)
          ) : (
            <></>
          )}
          <Grid item textAlign={'right'}>
            <IconButton onClick={handleApply}>
              <Check />
            </IconButton>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
}

function FilterFormString(
  filterValue: FilterValue | undefined,
  onChange: FilterChangedFunc,
  onEnterPressed?: () => void,
) {
  return (
    <Grid item flexGrow={1}>
      <TextField
        variant="outlined"
        size="small"
        value={filterValue?.value}
        onChange={(e) => {
          onChange({ ...filterValue, value: e.target.value });
        }}
        onKeyUp={(e) => {
          if (e.key == 'Enter' && onEnterPressed) onEnterPressed();
        }}
      />
    </Grid>
  );
}

function FilterFormNumber(
  filterValue: FilterValue | undefined,
  onChange: FilterChangedFunc,
  onEnterPressed?: () => void,
) {
  const [op, setOp] = useState<FilterTypeOperators>(filterValue?.operator ?? '=');
  return (
    <Grid item flexGrow={1}>
      <Grid container flexWrap={'nowrap'}>
        <Grid item>
          <Select
            variant="outlined"
            size="small"
            value={op}
            onChange={(e) => {
              const newOp = e.target.value as FilterTypeOperators;
              setOp(newOp);
              onChange({ ...filterValue, value: filterValue?.value, operator: newOp });
            }}
          >
            {(['=', '>', '>=', '<', '<=', 'not'] as FilterTypeOperators[]).map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item flexGrow={1}>
          <TextField
            variant="outlined"
            size="small"
            type="number"
            value={`${filterValue?.value ?? ''}`}
            onChange={(e) => {
              onChange({ ...filterValue, value: +e.target.value, operator: op });
            }}
            onKeyUp={(e) => {
              if (e.key == 'Enter' && onEnterPressed) onEnterPressed();
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

function FilterFormDate(filterValue: FilterValue | undefined, onChange: FilterChangedFunc) {
  return (
    <Grid item flexGrow={1}>
      <TextField
        variant="outlined"
        size="small"
        value={filterValue?.value}
        type="date"
        onChange={(e) => {
          onChange({ ...filterValue, value: e.target.value });
        }}
      />
    </Grid>
  );
}

function FilterFormBoolean(filterValue: FilterValue<Boolean> | undefined, onChange: FilterChangedFunc) {
  return (
    <Grid item flexGrow={1}>
      <Checkbox
        checked={Boolean(filterValue?.value)}
        onChange={(e) => onChange({ ...filterValue, value: e.target.checked })}
      />
    </Grid>
  );
}

function FilterFormValues(
  filterValue: FilterValue | undefined,
  values: string[],
  onChange: FilterChangedFunc,
  getCaption?: MappingFunc,
) {
  const [selected, setSelected] = useState<string[]>(filterValue?.value ?? []);
  useEffect(() => {
    setSelected([]);
  }, [values]);

  const toggleCheck = (item: string) => {
    let newValue = [];
    if (selected.includes(item)) {
      newValue = [...selected.filter((v) => v !== item)];
    } else {
      newValue = [...selected, item];
    }
    setSelected(newValue);
    onChange({ ...filterValue, value: newValue });
  };
  return (
    <Grid item flexGrow={1}>
      <List>
        {values?.map((item, index) => {
          return (
            <ListItem
              key={index}
              dense
              onClick={() => {
                toggleCheck(item);
              }}
            >
              <Checkbox checked={filterValue?.value && filterValue?.value.includes(item)} tabIndex={-1} disableRipple />
              <ListItemText primary={getCaption ? getCaption(item) : item} />
            </ListItem>
          );
        })}
      </List>
    </Grid>
  );
}
